import dayjs from "dayjs";
import {
	GenogramConnectionCategoryEnum,
	getGenogramConnectionCategoryByEdgeData,
} from "@shared/Genogram/GenogramConnectionCategoryEnum";

const EDGE_DATE_FORMAT_DEFAULT = "MMM DD, YYYY"
const EDGE_LABEL_FONT_SIZE = 16
const EDGE_LABEL_LINE_HEIGHT_MULTIPLIER = 1.2
const EDGE_LABEL_TOP_MARGIN = 6
const EDGE_DATE_FORMAT_OPTIONS = [
	{type: "year", format: "YYYY", label: "Year only"},
	{type: "month", format: "MMM YYYY", label: "Year and Month"},
	{type: "date", format: EDGE_DATE_FORMAT_DEFAULT, label: "Full Date"},
]

const ENDED_SOCIAL_CONNECTIONS = new Set([
	"family-divorce",
	"family-separation",
	"family-legal-separation",
	"family-engagement-separation",
	"family-nullity",
	"family-legal-cohabitation-separation",
	"family-legal-cohabitation-legal-separation",
	"family-cohabitation-separation",
	"family-non-sentimental-cohabitation-separation",
	"family-casual-separation",
])

export default class GenogramEdgeLabelManager {

	static syncEdgeLabel(edge){
		if(!edge || edge.length === 0){
			return
		}
		edge.data(this.getEdgeLabelData(edge.data()))
	}

	static syncAllEdgeLabels(cy){
		if(!cy){
			return
		}
		cy.edges().forEach(edge => this.syncEdgeLabel(edge))
	}

	static getEdgeLabelData(edgeData = {}){
		let edgeLabel = this.buildEdgeLabel(edgeData)
		return {
			edgeLabel,
			edgeLabelOrientation: this.isSocialConnection(edgeData) ? "horizontal" : "rotated",
			edgeLabelMarginY: this.calculateEdgeLabelMarginY(edgeData, edgeLabel),
			renderWidth: this.getEdgeRenderWidth(edgeData),
		}
	}

	static buildEdgeLabel(edgeData = {}){
		let note = (edgeData.note || "").trim()
		let periodsLabel = this.buildPeriodsLabel(edgeData.periods)
		return [note, periodsLabel].filter(Boolean).join("\n")
	}

	static calculateEdgeLabelMarginY(edgeData = {}, edgeLabel = ""){
		if(!edgeLabel){
			return 0
		}
		if(!this.isSocialConnection(edgeData)){
			return 0
		}

		let lineCount = edgeLabel.split("\n").filter(Boolean).length || 1
		let lineHeight = EDGE_LABEL_FONT_SIZE * EDGE_LABEL_LINE_HEIGHT_MULTIPLIER
		let totalHeight = lineCount * lineHeight
		return -(EDGE_LABEL_TOP_MARGIN + totalHeight / 2)
	}

	static buildPeriodsLabel(periods = []){
		let formattedPeriods = this.sortPeriodsChronologically(periods)
			.map(period => this.formatPeriod(period))
			.filter(Boolean)

		return formattedPeriods.join("; ")
	}

	static formatPeriod(period = {}){
		let startText = this.formatDateValue(period.startDate, period.startFormat)
		let hasEndDateEnabled = !!period.isEndDateEnabled || !!period.endDate
		let endText = hasEndDateEnabled ? this.formatDateValue(period.endDate, period.endFormat) : null

		if(startText && endText){
			return `${startText}-${endText}`
		}
		return startText || endText || ""
	}

	static formatDateValue(dateValue, format = EDGE_DATE_FORMAT_DEFAULT){
		if(!dateValue){
			return ""
		}
		return dayjs(dateValue).format(format || EDGE_DATE_FORMAT_DEFAULT)
	}

	static sortPeriodsChronologically(periods = []){
		return [...periods]
			.filter(period => period && (period.startDate || period.endDate))
			.sort((period1, period2) => {
				let start1 = period1.startDate || period1.endDate || Number.MAX_SAFE_INTEGER
				let start2 = period2.startDate || period2.endDate || Number.MAX_SAFE_INTEGER
				if(start1 !== start2){
					return start1 - start2
				}
				let end1 = period1.endDate || Number.MAX_SAFE_INTEGER
				let end2 = period2.endDate || Number.MAX_SAFE_INTEGER
				return end1 - end2
			})
	}

	static isSocialConnection(edgeData = {}){
		return getGenogramConnectionCategoryByEdgeData(edgeData) === GenogramConnectionCategoryEnum.SOCIAL
	}

	static isEndedSocialConnection(lineSubType = ""){
		return ENDED_SOCIAL_CONNECTIONS.has(lineSubType)
	}

	static getEdgeRenderWidth(edgeData = {}){
		let connectionCategory = getGenogramConnectionCategoryByEdgeData(edgeData, null)
		if(connectionCategory === GenogramConnectionCategoryEnum.SOCIAL){
			return 10
		}
		if(!edgeData.lineSubType){
			return null
		}
		return 28
	}
}

export {
	EDGE_DATE_FORMAT_DEFAULT,
	EDGE_DATE_FORMAT_OPTIONS,
}
